import { Component } from '@angular/core';
import { AfterViewInit, EventEmitter, Input, OnInit, Output, ViewChild, NgModule} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { FormControl, FormsModule, NgModel, ReactiveFormsModule,  } from '@angular/forms';
import { Farm, FarmRegisterRequestBody, RegisterFarmFormType, Employee, UserInfo, ProductType, FarmLocations } from '../../models/types';
import { CommonModule } from '@angular/common';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEnvelope, faAddressBook, faPhone, faXmark, faHome, faInfoCircle, faArrowRight, faArrowLeft, faPlus, faTrash} from '@fortawesome/free-solid-svg-icons';
import { FarmRegisterService } from '../services/farm-register.service';
import { LoginService } from '../services/login.service';
import Swal from 'sweetalert2';
import { MapComponent } from '../components/map/map.component';
import { MapService } from '../services/map.service';
import { ProfilePageService } from '../services/profile-page.service';
import { Router } from '@angular/router';


//TODO: Implement user autbentication to get userID

@Component({
  selector: 'app-farm-register',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FontAwesomeModule, MapComponent],
  templateUrl: './farm-register.component.html',
  styleUrl: './farm-register.component.scss'
})
export class FarmRegisterComponent {
  @ViewChild("contentContainer") contentContainer!: any;
  @Input() registerAdmin: boolean = false

  @Output() onCloseClicked: EventEmitter<any> = new EventEmitter<any>();

  currentStep: 'farmInfo' | 'employees' | 'products' = 'farmInfo';
  productTypes: ProductType[] = ['mlečni izdelek', 'zelenjava', 'meso', 'ostalo'];

  registrationFormGroup!: FormGroup<RegisterFarmFormType>;
  showRegistration = true;
  isRegistering = false;
  loggedInUserInfo: UserInfo | undefined;
  selectedLocation: FarmLocations | null = null;

  farmInfoFormGroup: FormGroup;
  employeesFormGroup: FormGroup;
  productsFormGroup: FormGroup;

  faXmark = faXmark;
  faHome = faHome;
  faAddressBook = faAddressBook;
  faPhone = faPhone;
  faEnvelope = faEnvelope;
  faInfoCircle = faInfoCircle;
  farmLocations: FarmLocations[] = [];
  currentFarmID: number | undefined;
  faArrowRight = faArrowRight;
  faArrowLeft = faArrowLeft;
  faPlus = faPlus;
  faTrash = faTrash;

  constructor(
    private fb: FormBuilder, 
    private farmRegisterService: FarmRegisterService, 
    private loginService: LoginService,
    private mapService: MapService,
    private profilePageService: ProfilePageService,
    private router: Router,) {
    // Initialize Form Groups
    this.farmInfoFormGroup = this.fb.group({
      farmName: ['', Validators.required],
      farmAddress: ['', Validators.required],
      farmPhone: ['', [Validators.required, Validators.pattern(/[0-9]{3}[\s\-]?[0-9]{3}[\s\-]?[0-9]{3}/)]],
      farmEmail: ['', [Validators.required, Validators.email]],
      farmDescription: ['']
    });

    this.employeesFormGroup = this.fb.group({
      employees: this.fb.array([
        this.fb.group({
          name: ['', Validators.required],
          surname: ['', Validators.required],
          employeeDescription: ['']
        })
      ])
    });

    this.productsFormGroup = this.fb.group({
      products: this.fb.array([
        this.fb.group({
          name: ['', Validators.required],
          description: [''],
          cost: [0],
          quantity: [0],
          type: ['', Validators.required],
          image: ['']
        })
      ])
    });
  }
  
  get farmName() { return this.farmInfoFormGroup.get('farmName'); }
  get farmAddress() { return this.farmInfoFormGroup.get('farmAddress'); }
  get farmPhone() { return this.farmInfoFormGroup.get('farmPhone'); }
  get farmEmail() { return this.farmInfoFormGroup.get('farmEmail'); }
  get employees(): FormArray { return this.employeesFormGroup.get('employees') as FormArray; }
  get products(): FormArray { return this.productsFormGroup.get('products') as FormArray; }

  ngOnInit() {
    this.loggedInUserInfo = this.loginService.GetLoggedInUserInfo();
  }

  proceedToEmployees(): void {
    if (this.farmInfoFormGroup.valid) {
      this.currentStep = 'employees';
    } else {
      Swal.fire('Error', 'Please fill out all required fields in Farm Information.', 'error');
    }
  }

  goBackToFarmInfo(): void {
    this.currentStep = 'farmInfo';
  }

  proceedToProducts(): void {
    if (this.employeesFormGroup.valid) {
      this.currentStep = 'products';
    } else {
      Swal.fire('Error', 'Please fill out all required fields in Employees section.', 'error');
    }
  }

  goBackToEmployees(): void {
    this.currentStep = 'employees';
  }

  addEmployee() {
    this.employees.push(this.fb.group({
      name: ['', Validators.required],
      surname: ['', Validators.required],
      employeeDescription: ['']
    }));
  }


  removeEmployee(index: number) {
    this.employees.removeAt(index);
  }

  addProduct() {
    this.products.push(this.fb.group({
      name: ['', Validators.required],
      description: [''],
      cost: [0],
      quantity: [0],
      type: ['', Validators.required]
    }));
  }

  // Remove a product from the products array
  removeProduct(index: number) {
    this.products.removeAt(index);
  }

  finishRegistration(): void {
    if (this.productsFormGroup.valid) {
      // Combine form data
      const farmData = this.combineFormData();

      this.isRegistering = true;

      this.farmRegisterService.registerFarm(farmData).subscribe(
        response => {
          Swal.fire('Uspeh', 'Uspešno ste registrirali kmetijo!', 'success');

          // Handle farm location logic
          this.handleFarmLocation();

          this.farmInfoFormGroup.reset();
          this.employeesFormGroup.reset();
          this.productsFormGroup.reset();

          this.isRegistering = false;
          const farmID = response.body?.farmID;
          this.router.navigate([`/admin-farm-profile`]);
        },
        error => {
          Swal.fire('Error', 'Failed to register farm. Please try again.', 'error');
          this.isRegistering = false;
        }
      );
    } else {
      Swal.fire('Error', 'Please fill out all required fields in Products section.', 'error');
    }
  }

  combineFormData(): FarmRegisterRequestBody {
    return {
      userID: this.loggedInUserInfo!.userID,
      name: this.farmInfoFormGroup.get('farmName')!.value,
      address: this.farmInfoFormGroup.get('farmAddress')!.value,
      phone: this.farmInfoFormGroup.get('farmPhone')!.value,
      email: this.farmInfoFormGroup.get('farmEmail')!.value,
      description: this.farmInfoFormGroup.get('farmDescription')!.value,
      employees: this.employees.value.map((emp: any) => ({
        employeeID: emp.employeeID,
        name: emp.name,
        surname: emp.surname,
        employeeDescription: emp.employeeDescription
      })),
      products: this.products.value.map((prod: any) => ({
        productID: prod.productID,
        name: prod.name,
        description: prod.description,
        cost: prod.cost,
        quantity: prod.quantity,
        type: prod.type,
        image: prod.image
      }))
    };
  }

  handleFarmLocation(): void {
    if (this.selectedLocation) {
      const loggedInUserID = this.loggedInUserInfo!.userID;
      this.profilePageService.getFarmInfo(loggedInUserID).subscribe(
        response => {
          const currentFarmID = response.body?.farmID;
          if (currentFarmID) {
            this.selectedLocation!.farmID = currentFarmID;
            this.mapService.AddFarmLocation(this.selectedLocation!).subscribe(
              response => console.log('Farm location added:', response),
              error => console.error('Error adding farm location:', error)
            );
          }
        },
        error => console.error('Error getting farm info:', error)
      );
    }
  }
  
  handleLocationSelected(location: FarmLocations): void {
    this.selectedLocation = location;
    console.log('Selected Farm Location:', this.selectedLocation);
    // You can perform further actions with the selected location here
  }

  OnRegisterCloseClicked() {
    // Implement the logic to close the form, if needed
  }
}
